// PR-J — TanStack Query hooks for the TPS Oversight staff console. Reads
// register under the co-located key factory; every mutation invalidates the
// relevant keys in onSuccess (stale-after-save convention). The emergency
// switch fans out widely because it voids the outgoing TPS's bills +
// distributions and flips the loan, touching every console read plus the
// loan-detail + tps caches.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  tpsOversightApi,
  type CreateTpsFeeScheduleRequest,
  type UpdateTpsFeeScheduleBody,
  type EmergencyTpsSwitchRequest,
} from "@/api/portfolioAdmin/tpsOversightApi";
import { tpsOversightKeys } from "@/hooks/portfolioAdmin/tpsOversightKeys";
import { tpsKeys } from "@/hooks/portfolioAdmin/tpsKeys";
import { portfolioAdminKeys } from "@/hooks/portfolioAdmin/usePortfolioAdmin";

// ——— Reads ———

export function useTpsOversightDashboard() {
  return useQuery({
    queryKey: tpsOversightKeys.dashboard(),
    queryFn: () => tpsOversightApi.dashboard(),
  });
}

export function useTpsApprovalQueue() {
  return useQuery({
    queryKey: tpsOversightKeys.approvalQueue(),
    queryFn: () => tpsOversightApi.approvalQueue(),
  });
}

export function useTpsCorrectionQueue() {
  return useQuery({
    queryKey: tpsOversightKeys.correctionQueue(),
    queryFn: () => tpsOversightApi.correctionQueue(),
  });
}

export function useTpsMoneyOversight() {
  return useQuery({
    queryKey: tpsOversightKeys.money(),
    queryFn: () => tpsOversightApi.money(),
  });
}

/** Section 3 — the clearing-account fund-safety anomalies (Guardrails section). */
export function useTpsAnomalies(acknowledged?: boolean) {
  return useQuery({
    queryKey: tpsOversightKeys.anomalies(acknowledged),
    queryFn: () => tpsOversightApi.anomalies(acknowledged),
  });
}

export function useTpsAssignedLoans(tpsId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: tpsOversightKeys.loansForTps(tpsId ?? ""),
    queryFn: () => tpsOversightApi.loansForTps(tpsId!),
    enabled: !!tpsId && enabled,
  });
}

export function useTpsFeeSchedule(tpsId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: tpsOversightKeys.feeSchedule(tpsId ?? ""),
    queryFn: () => tpsOversightApi.feeSchedule(tpsId!),
    enabled: !!tpsId && enabled,
  });
}

// ——— Mutations ———

export function useCreateTpsFeeSchedule(tpsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTpsFeeScheduleRequest) =>
      tpsOversightApi.createFeeSchedule(tpsId, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: tpsOversightKeys.feeSchedule(tpsId),
      });
    },
  });
}

/**
 * Section 3 — acknowledge a clearing-account anomaly. Releases the distribution
 * auto-hold for the TPS once no open anomalies remain, so invalidate the anomaly
 * lists AND the dashboard (payout-control posture / directory).
 */
export function useAcknowledgeAnomaly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tpsOversightApi.acknowledgeAnomaly(id),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: tpsOversightKeys.anomaliesRoot(),
      });
      void qc.invalidateQueries({ queryKey: tpsOversightKeys.dashboard() });
    },
  });
}

export function useUpdateTpsFeeSchedule(tpsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      feeId,
      body,
    }: {
      feeId: string;
      body: UpdateTpsFeeScheduleBody;
    }) => tpsOversightApi.updateFeeSchedule(tpsId, feeId, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: tpsOversightKeys.feeSchedule(tpsId),
      });
    },
  });
}

export function useDeleteTpsFeeSchedule(tpsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feeId: string) =>
      tpsOversightApi.deleteFeeSchedule(tpsId, feeId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: tpsOversightKeys.feeSchedule(tpsId),
      });
    },
  });
}

export function useSetCollectionsRecoveryRate(tpsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rate: number | null) =>
      tpsOversightApi.setCollectionsRecoveryRate(tpsId, rate),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: tpsOversightKeys.feeSchedule(tpsId),
      });
      void qc.invalidateQueries({ queryKey: tpsOversightKeys.dashboard() });
    },
  });
}

/**
 * Break-glass TPS switch for a loan. Voids the outgoing TPS's unpaid bills +
 * pending distributions and flips the loan immediately, so invalidate every
 * console read plus the loan-detail summary and the TPS registry caches.
 */
export function useEmergencyTpsSwitch(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmergencyTpsSwitchRequest) =>
      tpsOversightApi.emergencySwitch(loanId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tpsOversightKeys.dashboard() });
      void qc.invalidateQueries({ queryKey: tpsOversightKeys.approvalQueue() });
      void qc.invalidateQueries({
        queryKey: tpsOversightKeys.correctionQueue(),
      });
      void qc.invalidateQueries({ queryKey: tpsOversightKeys.money() });
      void qc.invalidateQueries({
        queryKey: portfolioAdminKeys.loanDetail(loanId),
      });
      void qc.invalidateQueries({ queryKey: tpsKeys.all });
    },
  });
}
